import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  Calendar, 
  FileText, 
  Wallet,
  LogOut,
  Scissors,
  Menu,
  X,
  CalendarDays,
  ClipboardList,
  Settings,
  BarChart3,
  Palette,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import axios from "axios";

const navItems = [
  { path: "/dashboard", label: "Desk", icon: LayoutDashboard },
  {
    path: "/agenda",
    label: "Agenda",
    icon: CalendarDays,
    children: [
      { path: "/cassa", label: "Cassa", icon: Wallet },
      { path: "/analisi", label: "Analisi", icon: BarChart3 },
    ]
  },
  { path: "/colorbar", label: "Color Bar", icon: Palette },
  { path: "/servizi", label: "Servizi", icon: Scissors },
  { path: "/clienti", label: "Clienti", icon: Users },
  { path: "/consulenze", label: "Consulenze", icon: ClipboardList },
  { path: "/magazzino", label: "Magazzino", icon: Package },
  { path: "/preventivi", label: "Preventivi", icon: FileText },
  { path: "/marketing-automazione", label: "Marketing", icon: Zap },
  { path: "/impostazioni", label: "Impostazioni", icon: Settings },
];

export default function Layout() {
  const { user, logout, token } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [salonSettings, setSalonSettings] = useState({
    nome_salone: "I CIRILLO PARRUCCHIERI",
    logo_url: null
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data) {
          setSalonSettings({
            nome_salone: res.data.nome_salone || "I CIRILLO PARRUCCHIERI",
            logo_url: res.data.logo_url || null
          });
        }
      } catch {
        // Use default
      }
    };
    if (token) fetchSettings();
  }, [token]);

  // Stato per il dropdown Agenda (sotto-voci) — aperto di default per scoprire la sotto-voce Cassa
  const [agendaOpen, setAgendaOpen] = useState(true);

  // Auto-apri il dropdown quando navighi su una sotto-voce
  useEffect(() => {
    if (location.pathname.startsWith("/cassa") || location.pathname.startsWith("/agenda")) {
      setAgendaOpen(true);
    }
  }, [location.pathname]);

  // Trova la label della pagina corrente (cerca anche tra i children)
  const findLabel = () => {
    for (const item of navItems) {
      if (location.pathname.startsWith(item.path)) return item.label;
      if (item.children) {
        const child = item.children.find(c => location.pathname.startsWith(c.path));
        if (child) return child.label;
      }
    }
    return "Desk";
  };
  const currentPage = findLabel();

  return (
    <div className="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Modern White Design */}
      <aside 
        className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`} 
        data-testid="sidebar"
      >
        {/* Logo Section con Toggle */}
        <div className="sidebar-logo">
          <div className={`flex items-center ${sidebarCollapsed ? 'flex-col gap-2' : 'gap-3'} flex-1`}>
            {salonSettings.logo_url ? (
              <img 
                src={salonSettings.logo_url.startsWith('http') ? salonSettings.logo_url : `${API.replace('/api', '')}${salonSettings.logo_url}`} 
                alt={salonSettings.nome_salone}
                className="w-11 h-11 rounded-2xl object-cover shadow-md"
              />
            ) : (
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Scissors className="w-5 h-5 text-white" />
              </div>
            )}
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <h1 className="text-base font-bold tracking-tight leading-tight text-slate-800 truncate">
                  {salonSettings.nome_salone}
                </h1>
                <p className="text-xs text-blue-600 font-medium">Gestionale Parrucchiere</p>
              </div>
            )}
            {/* Toggle Button sotto logo quando collapsed */}
            {sidebarCollapsed && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarCollapsed(false)}
                className="hidden md:flex bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700 rounded-xl h-8 w-8 shadow-sm"
                title="Espandi menu"
                data-testid="sidebar-toggle-expand"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
          {/* Toggle Button accanto quando espansa */}
          {!sidebarCollapsed && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarCollapsed(true)}
              className="hidden md:flex bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700 rounded-xl h-8 w-8 flex-shrink-0 shadow-sm"
              title="Comprimi menu"
              data-testid="sidebar-toggle-top"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                (item.path === "/dashboard" && location.pathname === "/");
              const hasChildren = item.children && item.children.length > 0;

              // Voce con sotto-voci (es. Agenda con Cassa sotto)
              if (hasChildren) {
                const anyChildActive = item.children.some(c => location.pathname.startsWith(c.path));
                const parentActive = isActive || anyChildActive;
                return (
                  <div key={item.path}>
                    <div className="flex items-stretch gap-0.5">
                      <NavLink
                        to={item.path}
                        onClick={() => {
                          setSidebarOpen(false);
                          setAgendaOpen(true);
                        }}
                        data-testid={`nav-${item.path.slice(1)}`}
                        className={`sidebar-nav-item group flex-1 ${parentActive ? 'active' : ''}`}
                        title={sidebarCollapsed ? item.label : ''}
                      >
                        <Icon className={`w-5 h-5 flex-shrink-0 ${parentActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-blue-500'}`} />
                        {!sidebarCollapsed && (
                          <span className="truncate">{item.label}</span>
                        )}
                        {isActive && !sidebarCollapsed && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-600" />
                        )}
                      </NavLink>
                      {!sidebarCollapsed && (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAgendaOpen(o => !o); }}
                          className="px-2 text-slate-400 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition"
                          aria-label={agendaOpen ? "Chiudi sottomenu" : "Apri sottomenu"}
                          data-testid={`nav-${item.path.slice(1)}-toggle`}
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform ${agendaOpen ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                    {/* Sotto-voci */}
                    {agendaOpen && !sidebarCollapsed && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-blue-100 pl-2">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = location.pathname.startsWith(child.path);
                          return (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              onClick={() => setSidebarOpen(false)}
                              data-testid={`nav-${child.path.slice(1)}`}
                              className={`sidebar-nav-item group text-sm ${childActive ? 'active' : ''}`}
                            >
                              <ChildIcon className={`w-4 h-4 flex-shrink-0 ${childActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-blue-500'}`} />
                              <span className="truncate">{child.label}</span>
                              {childActive && (
                                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-600" />
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                    {/* Sidebar compressa: mostra icone children sotto comunque (compatto) */}
                    {sidebarCollapsed && (
                      <div className="space-y-1 mt-1">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = location.pathname.startsWith(child.path);
                          return (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              onClick={() => setSidebarOpen(false)}
                              data-testid={`nav-${child.path.slice(1)}`}
                              className={`sidebar-nav-item group ${childActive ? 'active' : ''}`}
                              title={child.label}
                            >
                              <ChildIcon className={`w-5 h-5 flex-shrink-0 ${childActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-blue-500'}`} />
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              // Voce semplice
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.path.slice(1)}`}
                  className={`
                    sidebar-nav-item group
                    ${isActive ? 'active' : ''}
                  `}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-blue-500'}`} />
                  {!sidebarCollapsed && (
                    <span className="truncate">{item.label}</span>
                  )}
                  {isActive && !sidebarCollapsed && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-600" />
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {/* User Info */}
          <div className={`flex items-center gap-3 mb-3 ${sidebarCollapsed ? 'justify-center' : 'px-3 py-2'}`}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
              <span className="text-sm font-bold text-white">
                {user?.nome?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-700 truncate">{user?.nome}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            )}
          </div>
          
          {/* Logout & Collapse */}
          <div className={`flex ${sidebarCollapsed ? 'flex-col' : ''} gap-2`}>
            <Button 
              variant="ghost" 
              onClick={logout}
              data-testid="logout-btn"
              className={`${sidebarCollapsed ? 'w-full justify-center' : 'flex-1 justify-start'} text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl`}
              title={sidebarCollapsed ? 'Esci' : ''}
            >
              <LogOut className="w-4 h-4" />
              {!sidebarCollapsed && <span className="ml-2">Esci</span>}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden md:flex text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl"
              title={sidebarCollapsed ? 'Espandi' : 'Comprimi'}
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`} style={{ maxWidth: '100vw', overflowX: 'hidden' }}>
        {/* Header - Compact on Mobile */}
        <header className="page-header">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl flex-shrink-0"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-2xl font-bold tracking-tight text-slate-800 truncate">
                  {currentPage}
                </h2>
                <p className="text-xs sm:text-sm text-slate-400 mt-0.5 hidden sm:block">
                  {new Date().toLocaleDateString('it-IT', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
                <p className="text-xs text-slate-400 mt-0.5 sm:hidden">
                  {new Date().toLocaleDateString('it-IT', { 
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content - Responsive padding */}
        <div className="p-3 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
